import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './QuotePage.css';

type Step = 1 | 2 | 3;

interface QuoteFormData {
  serviceType: string;
  origin: string;
  destination: string;
  weight: string;
  timeline: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  notes: string;
}

interface FormErrors {
  serviceType?: string;
  origin?: string;
  destination?: string;
  name?: string;
  company?: string;
  email?: string;
  phone?: string;
}

const SERVICE_TYPES = [
  'Freight Transportation (FTL)',
  'Freight Transportation (LTL)',
  'Warehouse & Storage',
  'Last-Mile Delivery',
  'Custom Logistics Plan / Consultation',
];

const TIMELINES = ['ASAP', 'Within 1 week', 'Within 1 month', 'Flexible'];

const submitQuote = async (data: QuoteFormData): Promise<boolean> => {
  // TODO: replace /api/quote with your real backend endpoint
  try {
    const res = await fetch('/api/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.ok;
  } catch {
    return false;
  }
};

export const QuotePage = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);

  const [formData, setFormData] = useState<QuoteFormData>({
    serviceType: searchParams.get('type') === 'consultation'
      ? 'Custom Logistics Plan / Consultation'
      : '',
    origin: '',
    destination: '',
    weight: '',
    timeline: '',
    name: '',
    company: '',
    email: '',
    phone: '',
    notes: '',
  });

  useEffect(() => {
    stepHeadingRef.current?.focus();
  }, [step]);

  const set = (field: keyof QuoteFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validateStep1 = (): boolean => {
    const e: FormErrors = {};
    if (!formData.serviceType) e.serviceType = 'Please select a service type';
    if (!formData.origin.trim()) e.origin = 'Origin is required';
    if (!formData.destination.trim()) e.destination = 'Destination is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = (): boolean => {
    const e: FormErrors = {};
    if (!formData.name.trim()) e.name = 'Name is required';
    if (!formData.company.trim()) e.company = 'Company is required';
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Enter a valid email';
    if (!formData.phone.trim()) e.phone = 'Phone is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const ok = await submitQuote(formData);
    setIsLoading(false);
    if (ok) {
      setIsSubmitted(true);
    } else {
      setErrors({ name: 'Something went wrong. Please try again or call us directly.' });
    }
  };

  const STEP_LABELS: Record<Step, string> = {
    1: 'Shipment',
    2: 'Contact',
    3: 'Details',
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Quote Request Sent — Renuit Logistics</title>
        </Helmet>
        <div className="rn-quote-success">
          <div className="rn-quote-success__icon" aria-hidden="true">✓</div>
          <h1 className="rn-quote-success__heading">Request Received</h1>
          <p className="rn-quote-success__body">
            We'll review your details and respond within <strong>24 hours</strong>.
          </p>
          <p className="rn-quote-success__phone">
            In a hurry? Call us:{' '}
            <a href="tel:7866283731">(786) 628-3731</a>
          </p>
          <Link to="/" className="cs-button-solid">Back to Home</Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Get a Freight Quote — Renuit Logistics</title>
        <meta name="description" content="Get a SE US freight quote in under 2 minutes. FTL, LTL, 3PL warehousing, last-mile delivery. St. Petersburg, FL broker. 24-hour response." />
        <link rel="canonical" href="https://www.renuit.pro/quote" />
        <meta property="og:title" content="Get a Freight Quote — Renuit Logistics" />
        <meta property="og:description" content="Get a SE US freight quote in under 2 minutes. FTL, LTL, 3PL warehousing, last-mile delivery. St. Petersburg, FL broker. 24-hour response." />
        <meta property="og:url" content="https://www.renuit.pro/quote" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.renuit.pro/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RenuitLogistics" />
        <meta name="twitter:title" content="Get a Freight Quote — Renuit Logistics" />
        <meta name="twitter:description" content="Get a SE US freight quote in under 2 minutes. FTL, LTL, 3PL warehousing, last-mile delivery. St. Petersburg, FL broker. 24-hour response." />
        <meta name="twitter:image" content="https://www.renuit.pro/og-image.jpg" />
      </Helmet>

      <div className="rn-quote-page">
        <div className="rn-quote-wrap">
          {/* Header */}
          <div className="rn-quote-header">
            <span className="rn-quote-header__tag">Fast Response · No Commitment</span>
            <h1
              className="rn-quote-header__heading"
              ref={stepHeadingRef}
              tabIndex={-1}
            >
              Get a Freight Quote
            </h1>
            <p className="rn-quote-header__sub">Takes under 2 minutes. We'll respond within 24 hours.</p>
          </div>

          {/* Progress */}
          <div className="rn-quote-progress" role="list" aria-label="Form steps">
            {([1, 2, 3] as Step[]).map(s => (
              <div
                key={s}
                role="listitem"
                className={`rn-quote-progress__item ${step === s ? 'is-current' : ''} ${step > s ? 'is-done' : ''}`}
              >
                <div className="rn-quote-progress__dot">{step > s ? '✓' : s}</div>
                <span className="rn-quote-progress__label">{STEP_LABELS[s]}</span>
              </div>
            ))}
          </div>

          {/* Form */}
          <form className="rn-quote-form" onSubmit={handleSubmit} noValidate>

            {/* Step 1 */}
            {step === 1 && (
              <div className="rn-quote-step">
                <fieldset className="rn-quote-fieldset">
                  <legend className="rn-quote-legend">What type of shipment?</legend>
                  <div className="rn-quote-radio-group">
                    {SERVICE_TYPES.map(type => (
                      <label key={type} className={`rn-quote-radio ${formData.serviceType === type ? 'is-selected' : ''}`}>
                        <input
                          type="radio"
                          name="serviceType"
                          value={type}
                          checked={formData.serviceType === type}
                          onChange={set('serviceType')}
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                  </div>
                  {errors.serviceType && <p className="rn-quote-error">{errors.serviceType}</p>}
                </fieldset>

                <div className="rn-quote-row">
                  <div className="rn-quote-field">
                    <label htmlFor="origin" className="rn-quote-label">
                      Origin <span aria-hidden="true">*</span>
                    </label>
                    <input
                      id="origin"
                      type="text"
                      className={`rn-quote-input ${errors.origin ? 'has-error' : ''}`}
                      placeholder="City or ZIP code"
                      value={formData.origin}
                      onChange={set('origin')}
                      aria-required="true"
                      aria-describedby={errors.origin ? 'origin-err' : undefined}
                    />
                    {errors.origin && <p id="origin-err" className="rn-quote-error">{errors.origin}</p>}
                  </div>
                  <div className="rn-quote-field">
                    <label htmlFor="destination" className="rn-quote-label">
                      Destination <span aria-hidden="true">*</span>
                    </label>
                    <input
                      id="destination"
                      type="text"
                      className={`rn-quote-input ${errors.destination ? 'has-error' : ''}`}
                      placeholder="City or ZIP code"
                      value={formData.destination}
                      onChange={set('destination')}
                      aria-required="true"
                      aria-describedby={errors.destination ? 'destination-err' : undefined}
                    />
                    {errors.destination && <p id="destination-err" className="rn-quote-error">{errors.destination}</p>}
                  </div>
                </div>

                <div className="rn-quote-row">
                  <div className="rn-quote-field">
                    <label htmlFor="weight" className="rn-quote-label">Estimated Weight (lbs) <em>optional</em></label>
                    <input
                      id="weight"
                      type="text"
                      className="rn-quote-input"
                      placeholder="e.g. 5,000"
                      value={formData.weight}
                      onChange={set('weight')}
                    />
                  </div>
                  <div className="rn-quote-field">
                    <label htmlFor="timeline" className="rn-quote-label">Preferred Timeline</label>
                    <select
                      id="timeline"
                      className="rn-quote-input"
                      value={formData.timeline}
                      onChange={set('timeline')}
                    >
                      <option value="">Select timeline</option>
                      {TIMELINES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="rn-quote-step">
                <div className="rn-quote-row">
                  <div className="rn-quote-field">
                    <label htmlFor="name" className="rn-quote-label">Full Name <span aria-hidden="true">*</span></label>
                    <input
                      id="name"
                      type="text"
                      className={`rn-quote-input ${errors.name ? 'has-error' : ''}`}
                      placeholder="Jane Smith"
                      value={formData.name}
                      onChange={set('name')}
                      aria-required="true"
                    />
                    {errors.name && <p className="rn-quote-error">{errors.name}</p>}
                  </div>
                  <div className="rn-quote-field">
                    <label htmlFor="company" className="rn-quote-label">Company Name <span aria-hidden="true">*</span></label>
                    <input
                      id="company"
                      type="text"
                      className={`rn-quote-input ${errors.company ? 'has-error' : ''}`}
                      placeholder="Acme Corp"
                      value={formData.company}
                      onChange={set('company')}
                      aria-required="true"
                    />
                    {errors.company && <p className="rn-quote-error">{errors.company}</p>}
                  </div>
                </div>
                <div className="rn-quote-row">
                  <div className="rn-quote-field">
                    <label htmlFor="email" className="rn-quote-label">Email Address <span aria-hidden="true">*</span></label>
                    <input
                      id="email"
                      type="email"
                      className={`rn-quote-input ${errors.email ? 'has-error' : ''}`}
                      placeholder="jane@company.com"
                      value={formData.email}
                      onChange={set('email')}
                      aria-required="true"
                    />
                    {errors.email && <p className="rn-quote-error">{errors.email}</p>}
                  </div>
                  <div className="rn-quote-field">
                    <label htmlFor="phone" className="rn-quote-label">Phone Number <span aria-hidden="true">*</span></label>
                    <input
                      id="phone"
                      type="tel"
                      className={`rn-quote-input ${errors.phone ? 'has-error' : ''}`}
                      placeholder="(555) 000-0000"
                      value={formData.phone}
                      onChange={set('phone')}
                      aria-required="true"
                    />
                    {errors.phone && <p className="rn-quote-error">{errors.phone}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div className="rn-quote-step">
                <div className="rn-quote-step-summary">
                  <p><strong>Service:</strong> {formData.serviceType}</p>
                  <p><strong>Lane:</strong> {formData.origin} → {formData.destination}</p>
                  <p><strong>Contact:</strong> {formData.name} · {formData.email}</p>
                </div>
                <div className="rn-quote-field">
                  <label htmlFor="notes" className="rn-quote-label">
                    Anything else we should know?{' '}
                    <em>optional</em>
                  </label>
                  <textarea
                    id="notes"
                    className="rn-quote-input rn-quote-textarea"
                    placeholder="Hazmat? Special packaging? Recurring shipment? Let us know."
                    value={formData.notes}
                    onChange={set('notes')}
                    rows={5}
                  />
                </div>
              </div>
            )}

            {/* Nav buttons */}
            <div className="rn-quote-nav">
              {step > 1 && (
                <button type="button" className="rn-quote-back" onClick={handleBack}>
                  ← Back
                </button>
              )}
              {step < 3 && (
                <button type="button" className="cs-button-solid rn-quote-next" onClick={handleNext}>
                  Continue →
                </button>
              )}
              {step === 3 && (
                <button type="submit" className="cs-button-solid rn-quote-next" disabled={isLoading}>
                  {isLoading ? 'Sending…' : 'Submit Request'}
                </button>
              )}
            </div>
          </form>

          <p className="rn-quote-footer-note">
            Or call us directly: <a href="tel:7866283731">(786) 628-3731</a>
          </p>
        </div>
      </div>
    </>
  );
};
